import { ReactColumn, SortingRule, TableComponent } from '@components/table';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { buildStaticMapImageUrl } from '@core/static-map';
import { Plus } from 'lucide-react';
import React from 'react';
import { useParams } from 'react-router-dom';
import { CommunityLocationDto } from '@api/ChallengeClient';
import { DeleteLocationDialog } from './delete-location-dialog';
import { LocationDialog } from './location-dialog';
import { apiToGeoJson } from './geometry';
import { useStore } from './store';

const CommunityLocations = () => {
    const { communityId } = useParams();
    const parsedCommunityId = parseInt(communityId);
    const { locations, sorting, setSorting, setSearchTerm, dispatch, reload } =
        useStore(parsedCommunityId);

    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [editing, setEditing] = React.useState<
        CommunityLocationDto | undefined
    >(undefined);
    const [deleteTarget, setDeleteTarget] =
        React.useState<CommunityLocationDto | null>(null);

    const onSortingChange = (sorting?: SortingRule) => {
        if (sorting) {
            setSorting(sorting);
        }
    };

    const columns: ReactColumn<CommunityLocationDto>[] = [
        {
            Header: 'Map',
            id: 'map',
            disableSortBy: true,
            accessor: (row) => (
                <img
                    src={buildStaticMapImageUrl(apiToGeoJson(row), {
                        width: 200,
                        height: 120
                    })}
                    alt={`${row.name} area`}
                    width={200}
                    height={120}
                    loading="lazy"
                    onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display =
                            'none';
                    }}
                    className="block rounded"
                />
            )
        },
        {
            Header: 'Name',
            id: 'name',
            accessor: (row) => (
                <span className="text-sm font-semibold">{row.name}</span>
            )
        },
        {
            Header: 'Purpose',
            id: 'purpose',
            disableSortBy: true,
            accessor: (row) => (
                <div className="flex flex-col items-start gap-1">
                    {row.restrictedForWorkoutRecording && (
                        <Badge variant="secondary">Workout restricted</Badge>
                    )}
                    {row.recordedForArrivals && (
                        <Badge variant="secondary">Arrival tracking</Badge>
                    )}
                </div>
            )
        },
        {
            Header: 'Created',
            id: 'createdDate',
            accessor: (row) => (
                <span className="text-sm">
                    {new Date(row.createdDate).toLocaleDateString()}
                </span>
            )
        }
    ];

    return (
        <>
            <div className="mb-4 flex justify-end">
                <Button
                    onClick={() => {
                        setEditing(undefined);
                        setDialogOpen(true);
                    }}
                >
                    <Plus />
                    Create Location
                </Button>
            </div>

            <TableComponent<CommunityLocationDto[]>
                items={locations}
                columns={columns}
                emptyText="No locations configured for this community."
                searchPlaceholder="Search by name..."
                sorting={Array.isArray(sorting) ? sorting[0] : sorting}
                onSortingChange={onSortingChange}
                onSearchChange={setSearchTerm}
                onChangePage={(page) => dispatch({ type: 'page', page })}
                onPageSizeChange={(size) => dispatch({ type: 'size', size })}
                contextMenu={[
                    {
                        label: 'Edit',
                        action: (row) => {
                            setEditing(row);
                            setDialogOpen(true);
                        }
                    },
                    {
                        label: 'Delete',
                        action: (row) => setDeleteTarget(row)
                    }
                ]}
            />

            <LocationDialog
                open={dialogOpen}
                communityId={parsedCommunityId}
                location={editing}
                onClose={() => setDialogOpen(false)}
                onSaved={reload}
            />

            <DeleteLocationDialog
                location={deleteTarget}
                communityId={parsedCommunityId}
                onClose={() => setDeleteTarget(null)}
                onSuccess={() => {
                    setDeleteTarget(null);
                    reload();
                }}
            />
        </>
    );
};

export default CommunityLocations;
